import { useCallback, useEffect, useMemo } from "react";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { Button } from "@/shared/ui";
import {
  CUSTOMIZATION_FATHERS,
  CUSTOMIZATION_MOTHERS,
  CUSTOMIZATION_TRANS,
} from "@/domains/createCharacter/constants/general";
import {
  Identical,
  localizationIdenticalVariants,
} from "@/domains/createCharacter/constants/genetics";
import {
  currentIdenticalSelector,
  currentIdenticalVariantSelector,
  geneticsSelector,
  identicalVariantsSelector,
  nextStep,
  prevStep,
  setCorrectStep,
  setIdentical,
  setIdenticalVariant,
} from "@/domains/createCharacter/slice";
import useChangeGenetics from "@/domains/createCharacter/hooks/useChangeGenetics";
import CreateCharacterIdentical from "@/domains/createCharacter/components/CreateCharacterIdentical";
import CreateCharacterRange from "@/domains/createCharacter/components/CreateCharacterRange";
import CreateCharacterScrollArea from "@/domains/createCharacter/components/CreateCharacterScrollArea";
import CreateCharacterStepHeader from "@/domains/createCharacter/components/CreateCharacterStepHeader";
import CreateCharacterVariantSelect from "@/domains/createCharacter/components/CreateCharacterVariantSelect";

export default function CreateCharacterGeneticsContainer() {
  const dispatch = useAppDispatch();
  const identicalVariants = useAppSelector(identicalVariantsSelector);
  const currentIdenticalVariant = useAppSelector(currentIdenticalVariantSelector);
  const currentIdentical = useAppSelector(currentIdenticalSelector);
  const genetics = useAppSelector(geneticsSelector);
  const { randomizeAll, onChangeIdenticalView, onChangeSimilarity, onChangeSkin } =
    useChangeGenetics();

  useEffect(() => {
    dispatch(setCorrectStep({ step: 1, isCorrect: true }));
  }, [dispatch]);

  const identicalVariantsArray = useMemo(
    () => identicalVariants.map((_, index) => index),
    [identicalVariants],
  );

  useEffect(() => {
    if (!identicalVariants[currentIdenticalVariant].includes(currentIdentical)) {
      dispatch(setIdentical(identicalVariants[currentIdenticalVariant][0]));
    }
  }, [identicalVariants, currentIdenticalVariant, dispatch, currentIdentical]);

  const changeIdenticalVariantHandler = useCallback(
    (value: number) => {
      dispatch(setIdenticalVariant(value));
    },
    [dispatch],
  );

  const changeIdenticalHandler = useCallback(
    (value: Identical) => {
      dispatch(setIdentical(value));
    },
    [dispatch],
  );

  const parentsViews = useMemo(() => {
    // пока родители нормальные и это мама и папа, то выглядят они как мужчина и женщина соответсвенно
    // как только начинается 'генетический противоествественный мухлеж', то бабка может выглядеть как дедка и тд и тп
    if (
      identicalVariants[currentIdenticalVariant].includes(Identical.Father) &&
      identicalVariants[currentIdenticalVariant].includes(Identical.Mother)
    ) {
      return {
        fatherViewVariants: CUSTOMIZATION_FATHERS,
        motherViewVariants: CUSTOMIZATION_MOTHERS,
      };
    }

    return {
      fatherViewVariants: CUSTOMIZATION_TRANS,
      motherViewVariants: CUSTOMIZATION_TRANS,
    };
  }, [identicalVariants, currentIdenticalVariant]);

  const identicalValueTitle = useCallback(
    (value: number) => localizationIdenticalVariants[value],
    [],
  );

  const isHetero = useMemo(() => {
    const [father, mother] = identicalVariants[currentIdenticalVariant];

    return father === Identical.Father && mother === Identical.Mother;
  }, [identicalVariants, currentIdenticalVariant]);

  return (
    <>
      <CreateCharacterStepHeader title="Генетика" onRandom={randomizeAll} />
      <CreateCharacterScrollArea>
        <CreateCharacterVariantSelect
          label="Идентичность"
          variants={identicalVariantsArray}
          value={currentIdenticalVariant}
          valueTitle={identicalValueTitle}
          onChange={changeIdenticalVariantHandler}
          showVariantSelectForm={false}
          classNameArrowSwitcher="max-w-none"
          hint="Здесь вы выбираете двух самых близких родственников, на которых будет похож персонаж. На внешность повлияют только выбранные родственники."
        />

        <CreateCharacterIdentical
          variants={identicalVariants[currentIdenticalVariant]}
          selectedVariant={currentIdentical}
          onChange={changeIdenticalHandler}
        />
        {currentIdentical === Identical.Father && (
          <CreateCharacterVariantSelect
            label="Внешность отца"
            variants={parentsViews.fatherViewVariants}
            value={genetics.father}
            onChange={(value) => onChangeIdenticalView(Identical.Father, value as number)}
          />
        )}
        {currentIdentical === Identical.Mother && (
          <CreateCharacterVariantSelect
            label="Внешность матери"
            variants={parentsViews.motherViewVariants}
            value={isHetero ? genetics.mother : genetics.father}
            onChange={(value) => onChangeIdenticalView(Identical.Mother, value as number)}
          />
        )}
        {currentIdentical === Identical.GrandFather && (
          <CreateCharacterVariantSelect
            label="Внешность дедушки"
            variants={parentsViews.fatherViewVariants}
            value={genetics.mother}
            onChange={(value) => onChangeIdenticalView(Identical.GrandFather, value as number)}
          />
        )}
        {currentIdentical === Identical.GrandMother && (
          <CreateCharacterVariantSelect
            label="Внешность бабушки"
            variants={parentsViews.motherViewVariants}
            value={genetics.mother}
            onChange={(value) => onChangeIdenticalView(Identical.GrandMother, value as number)}
          />
        )}

        <CreateCharacterRange
          label="Схожесть"
          value={genetics.similarity}
          onChange={onChangeSimilarity}
        />
        <CreateCharacterRange label="Цвет кожи" value={genetics.skin} onChange={onChangeSkin} />
      </CreateCharacterScrollArea>
      <div className="grid grid-cols-2 items-center gap-2.5 pt-2 mt-auto">
        <Button variant="solid-surface-gradient" onClick={() => dispatch(prevStep())}>
          Назад
        </Button>
        <Button onClick={() => dispatch(nextStep())}>Далее</Button>
      </div>
    </>
  );
}
